import { openDB } from "idb";
import { api } from "./api";

const DB_NAME = "vytalidays";
const STORE = "checkin_queue";
const LEADS = "lead_queue";

let claimsCleared = false;

async function getDb() {
  const db = await openDb();
  if (!claimsCleared) {
    claimsCleared = true;
    // A tab closed mid-request can leave a row claimed; release stale claims on first use.
    for (const row of await db.getAll(STORE)) {
      if (row.syncing) await db.put(STORE, { ...row, syncing: false });
    }
  }
  return db;
}

async function openDb() {
  return openDB(DB_NAME, 2, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(LEADS)) {
        db.createObjectStore(LEADS, { keyPath: "client_submission_id" });
      }
    },
  });
}

const CODE_MAP_KEY = "vytali_event_codes";

/** Remembers short code -> event id for the events the Ambassador already opened, so an
 *  offline check-in typed by short code still carries the event id in the local queue. */
export function rememberEventCode(code, eventId) {
  if (!code || !eventId) return;
  try {
    const map = JSON.parse(localStorage.getItem(CODE_MAP_KEY) || "{}");
    map[String(code).toUpperCase()] = eventId;
    localStorage.setItem(CODE_MAP_KEY, JSON.stringify(map));
  } catch { /* storage unavailable — the short code match still works */ }
}

export function eventIdForCode(code) {
  try {
    const map = JSON.parse(localStorage.getItem(CODE_MAP_KEY) || "{}");
    return map[String(code || "").toUpperCase()] || null;
  } catch { return null; }
}

export async function queueCheckin(payload) {
  const db = await getDb();
  await db.add(STORE, {
    ...payload,
    event_id: payload.event_id || eventIdForCode(payload.code),
    client_checkin_id: payload.client_checkin_id || newId(),
    queued_at: new Date().toISOString(),
  });
}

export async function pendingCount() {
  const db = await getDb();
  return (await db.getAll(STORE)).length;
}

/** Local, informational only: a queued check-in is never a server-confirmed attendance. */
export async function pendingCheckins() {
  const db = await getDb();
  return db.getAll(STORE);
}

let syncingCheckins = false;

export async function syncQueue() {
  if (syncingCheckins) return 0;  // single in-flight pass: never replay the same row twice
  syncingCheckins = true;
  const db = await getDb();
  const items = await db.getAll(STORE);
  let synced = 0;
  try {
    for (const item of items) {
      // Claim the row locally before hitting the network so a parallel caller can't resend it.
      const claimed = await db.get(STORE, item.id);
      if (!claimed || claimed.syncing) continue;
      await db.put(STORE, { ...claimed, syncing: true });
      try {
        await api.post("/checkin", {
          code: item.code,
          event_id: item.event_id,
          client_timestamp: item.client_timestamp,
          client_checkin_id: item.client_checkin_id,
          offline: true,
        });
        await db.delete(STORE, item.id);
        synced += 1;
      } catch (e) {
        if (e?.response?.status && e.response.status < 500) {
          await db.delete(STORE, item.id);
        } else {
          await db.put(STORE, { ...claimed, syncing: false });
        }
      }
    }
  } finally {
    syncingCheckins = false;
  }
  return synced;
}

const newId = () =>
  (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

// ---------- offline manual leads ----------

export async function queueLead(lead) {
  const db = await getDb();
  const record = {
    ...lead,
    client_submission_id: lead.client_submission_id || newId(),
    captured_at: lead.captured_at || new Date().toISOString(),
    consent_at: lead.consent_at || new Date().toISOString(),
    queued_at: new Date().toISOString(),
    last_error: null,
  };
  await db.put(LEADS, record);
  return record;
}

export async function pendingLeads() {
  const db = await getDb();
  return db.getAll(LEADS);
}

export async function updateQueuedLead(id, patch) {
  const db = await getDb();
  const item = await db.get(LEADS, id);
  if (!item) return null;
  const next = { ...item, ...patch };
  await db.put(LEADS, next);
  return next;
}

export async function removeQueuedLead(id) {
  const db = await getDb();
  await db.delete(LEADS, id);
}

/** Replays queued leads through the same POST /me/leads engine. Idempotent by
 *  client_submission_id, so a retry never creates a second lead. Sensitive payloads are
 *  deleted locally as soon as the server confirms; failures stay queued. */
let syncingLeads = false;

export async function syncLeads() {
  if (syncingLeads) return { synced: 0, blocked: 0 };
  syncingLeads = true;
  const db = await getDb();
  const items = await db.getAll(LEADS);
  let synced = 0;
  let blocked = 0;
  try {
    for (const item of items) {
      if (item.needs_event) { blocked += 1; continue; }
      try {
        await api.post("/me/leads", {
          first_name: item.first_name,
          last_name: item.last_name || null,
          email: item.email || null,
          phone: item.phone || null,
          business_name: item.business_name || null,
          interest: item.interest || null,
          notes: item.notes || null,
          consent_accepted: true,
          language: item.language || "fr",
          event_id: item.event_id || null,
          client_submission_id: item.client_submission_id,
          captured_at: item.captured_at,
          consent_at: item.consent_at,
          offline: true,
        });
        await db.delete(LEADS, item.client_submission_id);
        synced += 1;
      } catch (e) {
        const status = e?.response?.status;
        if (status === 400 && /event/i.test(e?.response?.data?.detail || "")) {
          await db.put(LEADS, { ...item, needs_event: true, event_id: null, last_error: "event" });
          blocked += 1;
        } else if (status && status < 500 && status !== 429) {
          await db.put(LEADS, { ...item, last_error: e.response.data?.detail || "rejected" });
          blocked += 1;
        }
      }
    }
  } finally {
    syncingLeads = false;
  }
  return { synced, blocked };
}
